import React, { memo, useCallback, useState } from 'react';
import {
  MenuItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SelectFieldTrigger,
  Switch,
  ValueInputField,
} from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { useItem, useWriteContext } from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { InspectorSubLabel } from '../components/inspector-label';
import {
  getTextEffect,
  listTextEffects,
} from '../../items/text/effects/registry';
import type { TextEffectType } from '../../items/text/effects/types';
import type { NumberControlUpdateHandler } from './number-controls';

const NONE = 'none';

/**
 * 텍스트 "내용 기반 이펙트"(타자기 등) 컨트롤.
 * 효과 목록은 레지스트리(listTextEffects)에서 읽으므로, 새 효과를 등록하면 자동으로 노출된다.
 * 효과별 세부 config 컨트롤만 type 으로 분기한다(새 효과 추가 시 case 1개 추가).
 */
const TextEffectControlsUnmemoized: React.FC<{ itemId: string }> = ({
  itemId,
}) => {
  const t = useTranslations();
  const item = useItem(itemId);
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);
  const [open, setOpen] = useState(false);

  const effect = item?.type === 'text' ? item.effect : undefined;

  const setEffectType = useCallback(
    (value: TextEffectType | typeof NONE) => {
      const next =
        value === NONE ? undefined : getTextEffect(value)?.defaultConfig;
      setState({
        update: (state) =>
          changeItemsWithSelection(state, itemId, targetIds, (i) => {
            if (i.type !== 'text') return i;
            return { ...i, effect: next };
          }),
        commitToUndoStack: true,
      });
      setOpen(false);
    },
    [setState, itemId, targetIds],
  );

  const setTypewriterDuration = useCallback<NumberControlUpdateHandler>(
    ({ num, commitToUndoStack }) => {
      setState({
        update: (state) =>
          changeItemsWithSelection(state, itemId, targetIds, (i) => {
            if (i.type !== 'text' || i.effect?.type !== 'typewriter') return i;
            return { ...i, effect: { ...i.effect, durationInSeconds: num } };
          }),
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  const setTypewriterCursor = useCallback(
    (checked: boolean) => {
      setState({
        update: (state) =>
          changeItemsWithSelection(state, itemId, targetIds, (i) => {
            if (i.type !== 'text' || i.effect?.type !== 'typewriter') return i;
            return { ...i, effect: { ...i.effect, showCursor: checked } };
          }),
        commitToUndoStack: true,
      });
    },
    [setState, itemId, targetIds],
  );

  const setCountUpFrom = useCallback<NumberControlUpdateHandler>(
    ({ num, commitToUndoStack }) => {
      setState({
        update: (state) =>
          changeItemsWithSelection(state, itemId, targetIds, (i) => {
            if (i.type !== 'text' || i.effect?.type !== 'countUp') return i;
            return { ...i, effect: { ...i.effect, from: num } };
          }),
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  const setCountUpTo = useCallback<NumberControlUpdateHandler>(
    ({ num, commitToUndoStack }) => {
      setState({
        update: (state) =>
          changeItemsWithSelection(state, itemId, targetIds, (i) => {
            if (i.type !== 'text' || i.effect?.type !== 'countUp') return i;
            return { ...i, effect: { ...i.effect, to: num } };
          }),
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  const setCountUpDuration = useCallback<NumberControlUpdateHandler>(
    ({ num, commitToUndoStack }) => {
      setState({
        update: (state) =>
          changeItemsWithSelection(state, itemId, targetIds, (i) => {
            if (i.type !== 'text' || i.effect?.type !== 'countUp') return i;
            return { ...i, effect: { ...i.effect, durationInSeconds: num } };
          }),
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  if (!item || item.type !== 'text') return null;

  const currentDef = effect ? getTextEffect(effect.type) : undefined;
  const currentLabel = currentDef
    ? t(currentDef.label)
    : t('Editor.inspector.text.effect-none');

  return (
    <div className="flex flex-col gap-4xs">
      {/* 효과 선택 — 레지스트리 기반 드롭다운(없음 + 등록된 효과들) */}
      <div className="flex flex-col">
        <InspectorSubLabel>
          {t('Editor.inspector.text.effect')}
        </InspectorSubLabel>
        <Popover
          open={open}
          onOpenChange={setOpen}
        >
          <PopoverTrigger asChild>
            <SelectFieldTrigger value={currentLabel} />
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[var(--radix-popover-trigger-width)] min-w-[120px] rounded-xs border border-sys-style-neutral-default bg-sys-bg-surface-1 px-3xs py-2xs shadow-[0px_0px_16px_0px_rgba(4,4,5,0.08)]"
          >
            <MenuItem
              size="md"
              text={t('Editor.inspector.text.effect-none')}
              singleSelectType
              className="w-full rounded-xs"
              isSelected={!effect}
              onClick={() => setEffectType(NONE)}
            />
            {listTextEffects().map((def) => (
              <MenuItem
                key={def.type}
                size="md"
                text={t(def.label)}
                singleSelectType
                className="w-full rounded-xs"
                isSelected={effect?.type === def.type}
                onClick={() => setEffectType(def.type)}
              />
            ))}
          </PopoverContent>
        </Popover>
      </div>

      {/* 효과별 세부 설정 — 새 효과 추가 시 case 1개 추가 */}
      {effect?.type === 'typewriter' && (
        <div className="flex flex-col gap-4xs">
          <ValueInputField
            isShowLabel={true}
            label={t('Editor.inspector.text.effect-typewriter-duration')}
            value={effect.durationInSeconds}
            setValue={setTypewriterDuration}
            min={0.1}
            max={null}
            step={0.1}
            isUnit="s"
            accessibilityLabel={t(
              'Editor.inspector.text.effect-typewriter-duration',
            )}
            className="w-full"
          />
          <div className="flex items-center justify-between">
            <InspectorSubLabel>
              {t('Editor.inspector.text.effect-show-cursor')}
            </InspectorSubLabel>
            <Switch
              checked={effect.showCursor}
              onCheckedChange={setTypewriterCursor}
            />
          </div>
        </div>
      )}

      {effect?.type === 'countUp' && (
        <div className="flex flex-col gap-4xs">
          <ValueInputField
            isShowLabel={true}
            label={t('Editor.inspector.text.effect-countup-from')}
            value={effect.from}
            setValue={setCountUpFrom}
            min={null}
            max={null}
            step={1}
            accessibilityLabel={t('Editor.inspector.text.effect-countup-from')}
            className="w-full"
          />
          <ValueInputField
            isShowLabel={true}
            label={t('Editor.inspector.text.effect-countup-to')}
            value={effect.to}
            setValue={setCountUpTo}
            min={null}
            max={null}
            step={1}
            accessibilityLabel={t('Editor.inspector.text.effect-countup-to')}
            className="w-full"
          />
          <ValueInputField
            isShowLabel={true}
            label={t('Editor.inspector.text.effect-countup-duration')}
            value={effect.durationInSeconds}
            setValue={setCountUpDuration}
            min={0.1}
            max={null}
            step={0.1}
            isUnit="s"
            accessibilityLabel={t(
              'Editor.inspector.text.effect-countup-duration',
            )}
            className="w-full"
          />
        </div>
      )}
    </div>
  );
};

export const TextEffectControls = memo(TextEffectControlsUnmemoized);
